import { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from "react";
import { getLocalUser } from "../../lib/socket";
import { api } from "../../lib/api";
import type { AuthUser } from "../../types/auth";
import { toast } from "../../lib/ui";
import ChannelHeader from "../../components/chat/ChannelHeader";
import DesktopTitleBar, { DESKTOP_TITLE_BAR_HEIGHT } from "../../components/navigation/DesktopTitleBar";
import Composer from "../../components/chat/Composer";
import JumpToLatestButton from "../../components/chat/JumpToLatestButton";
import TypingIndicator from "../../components/chat/TypingIndicator";
import SharedItemsShelf from "../../components/media/SharedItemsShelf";
import { useKeyboardInsets } from "../../hooks/useKeyboardInsets";
import { useLatest } from "../../hooks/useLatest";
import VoiceChannelView from "../../components/voice/VoiceChannelView";
import VoiceDock from "../../components/voice/VoiceDock";
import MobileVoiceDock from "../../components/voice/MobileVoiceDock";
import VoiceSwitchModal from "../../components/voice/VoiceSwitchModal";
import VoiceTile from "../../components/voice/VoiceTile";
import type { Msg } from "../../types/chat";
import { useChannelLocks } from "../../hooks/useChannelLocks";
import { useMediaLightbox } from "../../hooks/useMediaLightbox";
import { useSocket } from "../../features/socket/SocketProvider";
import { useConnectionPresence } from "../socket/useConnectionPresence";
import { useSocketLifecycle } from "../socket/useSocketLifecycle";
import { useAccountSocketHandlers } from "./hooks/useAccountSocketHandlers";
import { useMessageSocketHandlers } from "./hooks/useMessageSocketHandlers";
import { useIncomingMessageHandlers } from "./hooks/useIncomingMessageHandlers";
import { useFavoritePreloads } from "./hooks/useFavoritePreloads";
import { useInPageNotification } from "../notifications/useInPageNotification";
import { useComposer } from "./composer/useComposer";
import { useMediaPickers } from "./composer/useMediaPickers";
import { useUploads } from "./composer/useUploads";
import { useAndroidShare } from "./hooks/useAndroidShare";
import { useMessageExtras } from "./hooks/useMessageExtras";
import { useOutbox } from "./hooks/useOutbox";
import { useSharedItems } from "./hooks/useSharedItems";
import { useChatSearch } from "./hooks/useChatSearch";
import { useAppearanceSettings } from "./hooks/useAppearanceSettings";
import { useVoiceCall } from "./hooks/useVoiceCall";
import { usePresence } from "./hooks/usePresence";
import { useUnread } from "./hooks/useUnread";
import { useNotifications } from "./hooks/useNotifications";
import { useFriends } from "./hooks/useFriends";
import { useDms } from "./hooks/useDms";
import { useSpaces } from "./hooks/useSpaces";
import { useChannelFolders } from "./hooks/useChannelFolders";
import { useChannelOrder } from "./hooks/useChannelOrder";
import { useFavorites } from "./hooks/useFavorites";
import { useQuickChannel } from "./hooks/useQuickChannel";
import { useKanbanData } from "./hooks/useKanbanData";
import { useFormData } from "./hooks/useFormData";
import { useGalleryView } from "./hooks/useGalleryView";
import { useResponsiveLayout } from "./hooks/useResponsiveLayout";
import { useMutedSpaces } from "./hooks/useMutedSpaces";
import { useUserStatusMessage } from "./hooks/useUserStatusMessage";
import { useInputModal } from "./hooks/useInputModal";
import { useComposerPaste } from "./hooks/useComposerPaste";
import { useNotesActions } from "./hooks/useNotesActions";
import { useChannelAdminActions } from "./hooks/useChannelAdminActions";
import { useChannelReorder } from "./hooks/useChannelReorder";
import { useLoadingHold } from "./hooks/useLoadingHold";
import DirectCallOverlay from "../calls/DirectCallOverlay";
import { getAuthToken } from "../../lib/auth";
import { setAudioRoute } from "../../lib/audioRoute";
import SpaceDiscoveryPanel from '../../components/navigation/SpaceDiscoveryPanel';
import { safeUuid } from "./chatUtils";
import { useChatSocketRegistration } from "./hooks/useChatSocketRegistration";
import { useMessageActions } from "./hooks/useMessageActions";
import { timeAgo, formatTime, getQuickEmojis } from "./messageFormatting";
import { useChannelNavigation, type ChannelNavigation } from "./hooks/useChannelNavigation";
import { useLastChannelBySpace } from "./hooks/useLastChannelBySpace";
import { useDefaultChannel } from "./hooks/useDefaultChannel";
import { useDirectCallWiring } from "./hooks/useDirectCallWiring";
import { useMessagePaging, MESSAGE_PAGE_SIZE } from "./hooks/useMessagePaging";
import { useChannelReadState } from "./hooks/useChannelReadState";
import { useSnapshotPersistence } from "./hooks/useSnapshotPersistence";
import { useServiceWorkerBridge } from "./hooks/useServiceWorkerBridge";
import { ChannelBody } from "./ChannelBody";
import { DesktopSidebars } from "./DesktopSidebars";
import { MobileSheets } from "./MobileSheets";
import { ChatOverlays } from "./ChatOverlays";
import { buildLandingFavoriteCards } from "./landingFavoriteCards";
import { renderPersonalItemPreview } from "./personalItemPreview";
import { linkifyMessage as linkifyMessageForMembers, renderLinkPreview } from "./messageRendering";
import type { BasicAttachment, PreviewItem, Channel, ServiceWorkerMessage } from "./chatTypes";
;

/* ======================================================================
   MONOLITH ENTRYPOINT (LEGACY): This file still owns nearly all app UI.
   Goal: peel pieces into feature modules; section markers help locate
   clusters until we can refactor.
   ====================================================================== */


/* ======================================================================
   LEGACY: ChatApp mega-component
   Owns sockets, routing, channels, voice, galleries, messages, composer.
   High priority to extract into feature slices (chat feed, composer,
   sidebars, voice, galleries, settings overlays).
   ====================================================================== */
// --- Authenticated app: all your previous logic lives here unconditionally ---
// Shared empty list for channels with no messages yet: a fresh [] each render made the effects that depend on `msgs` re-run every render.
const EMPTY_MSGS: Msg[] = [];

function ChatLayout({
  token,
  user,
  onTokenChange,
  onOpenProfile,
  onDismissProfileRoute,
  onOpenSettings,
  onDismissSettingsRoute,
}: {
  token: string;
  user: AuthUser | null;
  onTokenChange: (token: string | null) => void;
  onOpenProfile: (targetId: string) => void;
  onDismissProfileRoute?: () => void;
  onOpenSettings: (section?: string) => void;
  onDismissSettingsRoute?: () => void;
}) {
  const { socket, connect, disconnect } = useSocket();
  const { handleNotification } = useInPageNotification();
  const handleOpenProfile = useCallback((targetId: string | undefined | null) => {
    if (!targetId) return;
    onOpenProfile(targetId);
  }, [onOpenProfile]);
  useEffect(() => {
    try { localStorage.removeItem('__echo_unlocked_channels__'); } catch { /* noop */ }
  }, []);
  // identity
  const [me, setMe] = useState<{ userId?: string; name?: string; username?: string | null; avatarUrl?: string | null }>(() => getLocalUser());

  // Refs so the SW message handler can drive directCall actions even though
  // directCall is declared later in the component.
  const answerCallRef = useRef<(() => void) | null>(null);
  // Decline from the OS notification already hits /api/calls/decline inside the
  // service worker, so this only needs to dismiss the local incoming-call UI
  // (which also stops the ringtone). Re-calling declineCall here would double-hit
  // the API, 409, and leave the call UI/ring stuck.
  const dismissCallRef = useRef<(() => void) | null>(null);

  // Optional: handle postMessage from SW to open channels (basic example)
  useEffect(() => {
    const handleMessage = (event: Event) => {
      try {
        const msgEvent = event as MessageEvent<ServiceWorkerMessage>;
        const payload = msgEvent.data || {};
        if (payload.type === 'OPEN_CHANNEL' && payload.data?.channelId) {
          const rid = String(payload.data.channelId);
          openChannelFromFqidRef.current(rid);
        } else if (payload.type === 'MUTE_CHANNEL' && payload.data?.channelId) {
          const rid = String(payload.data.channelId);
          const spaceId = rid.includes(':') ? rid.split(':')[0] : rid;
          if (spaceId) setMutedSpaces(prev => ({ ...prev, [spaceId]: true }));
        } else if (payload.type === 'ACCEPT_CALL') {
          answerCallRef.current?.();
        } else if (payload.type === 'CALL_DECLINED') {
          dismissCallRef.current?.();
        }
      } catch {
        /* noop */
      }
    };
    if (navigator.serviceWorker) navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => { if (navigator.serviceWorker) navigator.serviceWorker.removeEventListener('message', handleMessage); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setMutedSpaces is a stable state setter returned by useMutedSpaces (called further down)
  }, []);

  // Voids / Channels
  const [currentVoidId, setCurrentVoidId] = useState<string>(() => {
    try {
      const v = localStorage.getItem('currentVoidId') || "";
      // Avoid auto-opening a DM space on fresh load/login
      return String(v).startsWith('dm_') ? "" : v;
    } catch { return ""; }
  });

  // Spaces drag/animation state
  const [channels, setChannels] = useState<Channel[]>([]);
  // Channel-folder state, its load effect, and the folder reorder/create
  // actions live in useChannelFolders (step 5.1a). setChannelFolders is
  // returned for switchVoid, which loads folders for the space it opens.
  const { channelFolders, setChannelFolders, handleFolderReorder, createFolderInline } = useChannelFolders({ currentVoidId, token });
  // chanOrder (per-space channel ordering) + channelTypeById (cross-space type
  // cache for favorites) + the channelOrderForSpace memo live in useChannelOrder
  // (step 5.1b). Their setters are returned for the parent writers that stay:
  // onChannelList, handleChannelReorder, and snapshot hydration.
  const { chanOrder, setChanOrder, channelTypeById, setChannelTypeById, channelOrderForSpace } = useChannelOrder({ currentVoidId });
  const [currentChannelId, setCurrentChannelId] = useState<string>(() => {
    try { return localStorage.getItem('currentChannelId') || "general"; } catch { return "general"; }
  });
  const k = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
  const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
  // switchVoid / switchChannel / openChannelFromFqid live in useChannelNavigation, which has to be
  // called after the hooks it reads (voice, channel locks, read state) while those hooks, and the
  // socket handlers, receive these three. These wrappers forward to the latest implementation;
  // navigationRef is filled in where useChannelNavigation is called. Nothing calls them during render.
  const navigationRef = useRef<ChannelNavigation | null>(null);
  function switchVoid(id: string) { navigationRef.current?.switchVoid(id); }
  function switchChannel(id: string) { navigationRef.current?.switchChannel(id); }
  function openChannelFromFqid(fqid: string, fallbackVoidId?: string) { navigationRef.current?.openChannelFromFqid(fqid, fallbackVoidId); }
  // Track current ids in refs to avoid race in async/socket handlers
  const currentVoidIdRef = useRef<string>(currentVoidId);

  useEffect(() => { currentVoidIdRef.current = currentVoidId; }, [currentVoidId]);
  const currentChannelIdRef = useRef<string>(currentChannelId);
  useEffect(() => { currentChannelIdRef.current = currentChannelId; }, [currentChannelId]);
  // Mirrors for the stable socket-lifecycle effect (runs once per connection, not
  // per channel switch) so onConnect can read the live channel list and navigate
  // deep links without taking currentVoidId/currentChannelId as effect deps.
  const channelsRef = useRef<Channel[]>(channels);
  useEffect(() => { channelsRef.current = channels; }, [channels]);
  // openChannelFromFqid is a hoisted function declaration recreated each render;
  // keep a ref to the latest so the stable onConnect deep-link path isn't stale.
  const openChannelFromFqidRef = useRef<(fqid: string, fallbackVoidId?: string) => void>(() => {});
  useEffect(() => { openChannelFromFqidRef.current = openChannelFromFqid; });
  // Space list/order/roles/hidden/create state lives in useSpaces (extracted
  // in step 4.3). switchVoid deliberately stayed below in the composition
  // layer — it's cross-cluster navigation and is mutually entangled with
  // useChannelLocks (see the hook's doc comment).
  const {
    voids,
    setVoids,
    spaceOrder,
    setSpaceOrder,
    hiddenSpaces,
    spaceRoles,
    currentSpace,
    nonDmVoids,
    isSpaceOwner,
    canAccessChannel,
    createSpaceOpen,
    setCreateSpaceOpen,
    createSpace,
    searchSpaces,
    requestJoinSpace,
    joinSpaceWithCode,
    handleCreateSpace,
  } = useSpaces({
    socket,
    token,
    me,
    user,
    currentVoidId,
    currentVoidIdRef,
    setCurrentVoidId,
    setCurrentChannelId,
  });

  function notifyNativeChannelViewed(spaceId: string, channelId: string) {
    if (!spaceId || !channelId) return;
    const bridge = (window as any)?.EchoAndroid;
    if (bridge && typeof bridge.onChannelViewed === "function") {
      try {
        bridge.onChannelViewed(spaceId, fq(spaceId, channelId));
      } catch {
        // ignore native bridge errors
      }
    }
  }


  const {
    setLockedChannelMap,
    pendingChannelSwitchRef,
    unlockModal,
    unlockingChannel,
    maybePromptChannelUnlock,
    closeUnlockModal,
    handleUnlockChannel,
  } = useChannelLocks({ fq, token, currentVoidIdRef, switchVoid, switchChannel });

  // Messages
  const [msgsByKey, setMsgsByKey] = useState<Record<string, Msg[]>>({ ["home:general"]: [] });
  // Mirror of msgsByKey for non-reactive reads (e.g. markChannelRead recovering
  // the last loaded message id without re-creating its callback on every message).
  const msgsByKeyRef = useRef(msgsByKey);
  useEffect(() => { msgsByKeyRef.current = msgsByKey; }, [msgsByKey]);
  const listRef = useRef<HTMLDivElement>(null);
  const msgs = msgsByKey[k(currentVoidId, currentChannelId)] ?? EMPTY_MSGS;
  // History is undefined until it arrives (then an array, possibly empty). Capped so a channel that never answers can't spin forever.
  const messagesLoading = useLoadingHold(!!currentVoidId && msgsByKey[k(currentVoidId, currentChannelId)] === undefined, k(currentVoidId, currentChannelId), 8000);

  // Scroll to show receipts/reactions that appear on the last message after React renders.
  // We derive stable primitives so the effect only fires when these fields actually change.
  const lastMsg = msgs[msgs.length - 1];
  const lastMsgId = lastMsg?.id;
  const lastMsgReactionKeys = lastMsg?.reactions ? Object.entries(lastMsg.reactions).map(([e, v]) => `${e}:${v.count}`).join(',') : '';
  const lastMsgSeenByIds = lastMsg?.seenByIds ? lastMsg.seenByIds.join(',') : '';
  useEffect(() => {
    if (!lastMsgId) return;
    const el = listRef.current;
    if (!el) return;
    // Don't interrupt a playing video
    const hasPlayingVideo = Array.from(el.querySelectorAll('video')).some(v => !v.paused);
    if (hasPlayingVideo) return;
    // Only nudge if the user was already near the bottom — don't interrupt deliberate scrolling
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 120) {
      el.scrollTop = el.scrollHeight;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMsgId, lastMsgReactionKeys, lastMsgSeenByIds]);

  // Per-message reaction/popover view state (step 6.2): the reaction hover
  // tooltip (+ its loader), the reaction emoji picker (+ click-outside), revealed
  // spoilers, and the link-preview cache (+ the prefetch effect driven by `msgs`).
  // Render/action helpers (linkifyMessage/renderLinkPreview/toggleReaction + the
  // tooltip JSX) stay in this layer and read these values; the hover/picker/
  // spoiler setters are returned so the message list can drive them.
  const {
    linkPreviewMap,
    reactionInfoByMsg,
    loadReactionsFor,
    hoverReaction,
    setHoverReaction,
    reactionTipPos,
    setReactionTipPos,
    revealedSpoilers,
    setRevealedSpoilers,
    pickerFor,
    setPickerFor,
    reactionBtnRef,
    reactionMenuRef,
  } = useMessageExtras(msgs);
  useSnapshotPersistence({ currentVoidId, currentChannelId, channels, msgsByKey, setChannels, setChanOrder, setCurrentChannelId, setMsgsByKey });

  useServiceWorkerBridge({ token, currentVoidId, currentChannelId });
  const stripSelfSeen = useCallback((m: Msg): Msg => {
    if (!me?.userId) return m;
    const uid = me.userId;
    let changed = false;
    let seenByIds = m.seenByIds;
    if (seenByIds?.includes(uid)) {
      seenByIds = seenByIds.filter((id) => id !== uid);
      changed = true;
    }
    let seenBy = m.seenBy;
    if (seenBy?.length) {
      const trimmed = seenBy.filter((n) => (n || '').trim().toLowerCase() !== (me.name || me.userId || '').trim().toLowerCase() && (n || '') !== uid);
      if (trimmed.length !== seenBy.length) {
        seenBy = trimmed;
        changed = true;
      }
    }
    return changed ? { ...m, seenBy, seenByIds } : m;
  }, [me?.name, me?.userId]);
  // typing + presence — state/handlers live in usePresence (extracted in step 3.1).
  // The render-smoothing lastTypingTextRef moved into TypingIndicator (step 7.6.9).
  const {
    typers,
    setTypers,
    roomUserIds,
    setRoomUserIds,
    spaceUserIds,
    globalUserIds,
    roomMobileIds,
    spaceMobileIds,
    globalMobileIds,
    roomIdleIds,
    spaceIdleIds,
    globalIdleIds,
  } = usePresence({ socket, currentVoidId, currentChannelId });
  const [members, setMembers] = useState<{ id: string; name: string; username?: string; avatarUrl?: string | null; status?: string; nameColor?: string | null; role?: string; bio?: string; roles?: { id: string; name: string; color?: string | null }[] }[]>([]);
  // unread / notified / unread-divider state + persistence live in useUnread
  // (extracted in step 3.2). The socket handlers that bump them (onNew /
  // onSpaceNotify) stay below and consume markUnread/notifiedRef/setNotified;
  // markChannelRead, navigation, and the scroll handlers clear via the setters.
  const {
    unread,
    setUnread,
    markUnread,
    notified,
    setNotified,
    notifiedRef,
    unreadMarkerByChan,
    setUnreadMarkerByChan,
  } = useUnread({ userId: me.userId });
  // Track recent incoming messages per channel to prevent duplicate renders (by id or rapid same-content from same author)
  const recentIncomingRef = useRef<Record<string, { ids: Set<string>; recents: { authorId?: string; content: string; ts: number }[] }>>({});
  // Throttle quick sends from the landing favorites widget (per channel + content)
  const quickSendGuardRef = useRef<Record<string, number>>({});
  const quickSendLastTextRef = useRef<Record<string, string>>({});
  const { pendingReadRef, markChannelRead, markUnreadTracked, readReceiptsEnabledRef, selfInvisibleRef } = useChannelReadState({
    socket, msgs, msgsByKeyRef, listRef, currentVoidId, currentChannelId, members, userId: me.userId, markUnread, setNotified, setUnread,
  });
  const [seenTip, setSeenTip] = useState<{ x: number; y: number; ids: string[] } | null>(null);
  const {
    imagePreview,
    setImagePreview,
    imagePreviewVisible,
    imagePreviewList,
    setImagePreviewList,
    imagePreviewIndex,
    setImagePreviewIndex,
    closeImagePreview,
    videoPreview,
    setVideoPreview,
    videoPreviewVisible,
    closeVideoPreview,
  } = useMediaLightbox();


  // Responsive layout chrome (step 7.1): mobile sheets, the swipe-to-open
  // gesture, desktop column resizers, the md breakpoint, and the
  // Composer-measured height. channelPaneView + the chanSheetOpen↔channelPaneView
  // reconciliation effects stay below (navigation-tangled) and consume the
  // returned chanSheetOpen/setChanSheetOpen.
  const {
    chanSheetOpen, setChanSheetOpen,
    usersSheetOpen, setUsersSheetOpen,
    swipePanel, swipeMode, swipeProgress,
    chanW, peopleW, startDrag,
    isMdUp, isWideUp,
    composerHeight, setComposerHeight,
  } = useResponsiveLayout({ currentVoidId, listRef });
  // In-chat DM search
  const {
    open: chatSearchOpen, setOpen: setChatSearchOpen,
    query: chatSearchQuery, setQuery: setChatSearchQuery,
    filter: chatSearchFilter, setFilter: setChatSearchFilter,
    fromUser: chatSearchFromUser, setFromUser: setChatSearchFromUser,
    results: chatSearchResults, loading: chatSearchLoading,
    scrollToId: chatScrollToId, setScrollToId: setChatScrollToId,
    highlightId: chatHighlightId, setHighlightId: setChatHighlightId,
    inContextMode: chatInContextMode, setInContextMode: setChatInContextMode,
    containerRef: chatSearchRef,
  } = useChatSearch({ currentVoidId, currentChannelId });
  const { tabBgClass, showDrawerTabs } = useAppearanceSettings();
  const [channelPaneView, setChannelPaneView] = useState<'spaces' | 'dms'>(() => {
    try {
      const raw = localStorage.getItem('channelPaneView');
      if (raw === 'spaces' || raw === 'dms') return raw;
      return 'spaces';
    } catch { return 'spaces'; }
  });
  // DM page/search/start/hidden state lives in useDms (extracted in step 4.2).
  // Called here — after channelPaneView, before the render-time
  // landingFavoriteCards loop that calls getDmDisplayName. switchVoid/
  // switchChannel are hoisted function declarations (safe to pass above their
  // definitions); setDmFriendSearch is wrapped in an arrow because useFriends
  // is called further down — the wrapper only dereferences it inside
  // startDmWithUser, post-render.
  const {
    dmPageOpen,
    setDmPageOpen,
    dmSearch,
    setDmSearch,
    dmSearchOpen,
    setDmSearchOpen,
    dmStartBusyUserId,
    startDmWithUser,
    activeDmVoids,
    getDmDisplayName,
  } = useDms({
    socket,
    me,
    user,
    voids,
    switchVoid,
    switchChannel,
    setChannelPaneView,
    setChanSheetOpen,
    setDmFriendSearch: (v) => setDmFriendSearch(v),
  });

  const showPeople = !!currentVoidId;
  const [peoplePanelCollapsed, setPeoplePanelCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('peoplePanelCollapsed') === 'true'; } catch { return false; }
  });

  // Disable legacy mini status background prefetch and local seeding (status now derives from bio only)
  useEffect(() => { /* no-op: mini status disabled */ }, [members]);
  useEffect(() => { /* no-op: mini status disabled */ }, [me?.userId]);

  // Mobile swipe-to-open gesture moved to useResponsiveLayout (step 7.1).
  useEffect(() => { try { localStorage.setItem('channelPaneView', channelPaneView); } catch { /* noop */ } }, [channelPaneView]);
  // Persist current selection so refresh restores the view
  useEffect(() => { try { localStorage.setItem('currentVoidId', currentVoidId); } catch { /* noop */ } }, [currentVoidId]);
  useEffect(() => { try { localStorage.setItem('currentChannelId', currentChannelId); } catch { /* noop */ } }, [currentChannelId]);
  const handleProfileDmTarget = useCallback(() => {
    try {
      const raw = localStorage.getItem('__profile_dm_target');
      if (!raw) return;
      localStorage.removeItem('__profile_dm_target');
      const target = JSON.parse(raw);
      if (!target?.spaceId || !target?.channelId) return;
      const channelShort = target.channelId.includes(':') ? target.channelId.split(':')[1] : target.channelId;
      setCurrentVoidId(target.spaceId);
      setChannelPaneView(String(target.spaceId).startsWith('dm_') ? 'dms' : 'spaces');
      setCurrentChannelId(channelShort);
      socket.emit('void:switch', { voidId: target.spaceId });
      socket.emit('channel:list', { voidId: target.spaceId });
      socket.emit('channel:switch', { voidId: target.spaceId, channelId: target.channelId });
    } catch {
      /* noop */
    }
  }, [socket]);
  useEffect(() => {
    handleProfileDmTarget();
    const listener = () => handleProfileDmTarget();
    window.addEventListener('echo:profile-dm-target', listener);
    return () => window.removeEventListener('echo:profile-dm-target', listener);
  }, [handleProfileDmTarget]);
  const handlePendingInviteTarget = useCallback(() => {
    try {
      const raw = localStorage.getItem('__pending_invite_target');
      if (!raw) return;
      localStorage.removeItem('__pending_invite_target');
      const target = JSON.parse(raw);
      const sid = String(target?.spaceId || '').trim();
      if (!sid) return;
      setCurrentVoidId(sid);
      setChannelPaneView('spaces');
      setCurrentChannelId('general');
      socket.emit('void:switch', { voidId: sid });
      socket.emit('channel:list', { voidId: sid });
      socket.emit('channel:switch', { voidId: sid, channelId: `${sid}:general` });
    } catch {
      /* noop */
    }
  }, [socket]);
  useEffect(() => {
    handlePendingInviteTarget();
    const listener = () => handlePendingInviteTarget();
    window.addEventListener('echo:invite-target', listener);
    return () => window.removeEventListener('echo:invite-target', listener);
  }, [handlePendingInviteTarget]);
  // Focus heartbeat, hide/show disconnect, network reconnects and idle detection live in useConnectionPresence.
  useConnectionPresence({ socket, connect, disconnect, currentVoidIdRef, currentChannelIdRef, notifyChannelViewed: notifyNativeChannelViewed });
  // Resizable columns (chanW/peopleW) + startDrag moved to useResponsiveLayout (step 7.1).


  // Composer draft state (text + textarea auto-resize/inputRef, spoiler toggle,
  // inline edit buffer, reply target, quick-reaction favEmojis) lives in
  // useComposer (step 6.1). The send/edit/reply *logic* stays in this layer and
  // reads these values (message cluster, 6.3); the edit/reply setters are
  // returned so the message list can drive them (the narrow interface).
  const {
    text,
    setText,
    inputRef,
    spoiler,
    setSpoiler,
    editingId,
    setEditingId,
    editText,
    setEditText,
    replyTo,
    setReplyTo,
    favEmojis,
  } = useComposer<Msg>();
  const currentChannel = channels.find((c) => c.id === currentChannelId);
  const currentChannelType = useMemo(() => (currentChannel?.type || '').toLowerCase(), [currentChannel?.type]);
  const isMessageChannelType = !currentChannelType || currentChannelType === 'text' || currentChannelType === 'dm' || currentChannelType === 'announcement';
// Per-channel kanban + channel-tag view state + loaders live in useKanbanData
// (step 5.2a). Setters/loaders are returned for the parent callers that stay:
// onChannelList/switchChannel (loadKanbanIfNeeded), the favorites kanban-preload
// + retry-empty effects (loadKanbanIfNeeded/reloadKanban), and the kanban/form
// view JSX (mutate*/fetchChannelTags).
const {
  kanbanByChan,
  setKanbanByChan,
  channelTagsByChan,
  setChannelTagsByChan,
  fetchChannelTags,
  loadKanbanIfNeeded,
  reloadKanban,
  mutateKanbanLists,
  mutateChannelTags,
} = useKanbanData();
const currentChannelFqid = currentVoidId && currentChannelId ? fq(currentVoidId, currentChannelId) : null;
const currentKanbanFqid = currentChannelType === 'kanban' ? fq(currentVoidId, currentChannelId) : null;
const currentKanbanLists = currentKanbanFqid ? (kanbanByChan[currentKanbanFqid] || []) : [];
const currentKanbanTags = currentKanbanFqid ? (channelTagsByChan[currentKanbanFqid] || []) : [];
const currentChannelTags = currentChannelFqid ? (channelTagsByChan[currentChannelFqid] || []) : [];
  const {
    pendingUploads,
    uploading,
    uploadError,
    addUploads,
    removePending: handleRemovePending,
    clearPending: clearPendingUploads,
    setPendingUploads,
  } = useUploads();
  // Per-channel form (Questions) view state + loaders live in useFormData (step
  // 5.2b). setFormByChan + scheduleFormReload are returned for the parent socket
  // handlers (onFormState/onFormAnswer); loadFormIfNeeded for
  // onChannelList/switchChannel; reloadForm for the 5s form-refresh interval.
  // `formByChan` is intentionally not destructured here: it is never read at render
  // (FormChannelView fetches its own data). The cache is consumed only inside the
  // hook as loadFormIfNeeded's dedup guard; fully removing it means gutting the
  // onFormState/onFormAnswer handlers in the deferred spine socket effect → 7.3.
  const { setFormByChan, scheduleFormReload, loadFormIfNeeded, reloadForm } = useFormData();
  // Track local submission state (per-question) for the current user to show a checkmark after pressing Enter
  // (Removed channel-wide blind mode; using per-question lock instead)
  // Favorites: fully-qualified channel ids (e.g. "space123:general" or "dm_abc:chat")
  // Favorite-channel id list (+ kanban-list favorites) and its server/local
  // persistence + membership helpers live in useFavorites (step 5.1d). The
  // favorites-coupled effects below (kanban preload, previews, autoscroll) stay
  // here and read the returned list — they belong to kanban/landing-widget
  // concerns, not favorites-state ownership.
  const { favorites, setFavorites, isFav, toggleFav, isListFav, toggleListFav, parseListFav } = useFavorites({ token });

  // Lightweight previews fed by user:notify for channels we might not be subscribed to
  // Lightweight previews fed by user:notify for channels we might not be subscribed to
  const [previewsByChan, setPreviewsByChan] = useState<Record<string, PreviewItem[]>>({});
  const addPreview = useCallback((fqChanId: string, preview: PreviewItem) => {
    setPreviewsByChan((old) => {
      const list = old[fqChanId] ? [...old[fqChanId], preview] : [preview];
      while (list.length > 20) list.shift();
      return { ...old, [fqChanId]: list };
    });
    // Auto-scroll landing preview if this channel is a favorite
    setTimeout(() => {
      const el = favScrollRefs.current[fqChanId];
      try { if (el) el.scrollTop = el.scrollHeight; } catch {}
    }, 0);
  }, []);
  async function loadPreviewIfNeeded(fqChanId: string, force = false) {
    try {
      if (!force && Object.prototype.hasOwnProperty.call(previewsByChan, fqChanId)) return;
      const tok = getAuthToken() || '';
      const res = await api.getAuth(`/channels/preview?channelId=${encodeURIComponent(fqChanId)}&limit=15`, tok);
      const msgs: { id?: string; content?: string; authorName?: string; authorId?: string; authorColor?: string | null; createdAt?: string; attachments?: BasicAttachment[] }[] =
        Array.isArray(res?.messages) ? (res.messages as { id?: string; content?: string; authorName?: string; authorId?: string; authorColor?: string | null; createdAt?: string; attachments?: BasicAttachment[] }[]) : [];
      const mapped: PreviewItem[] = msgs.map((m) => ({
        id: String(m?.id ?? crypto.randomUUID()),
        content: String(m?.content ?? ''),
        authorName: m?.authorName ? String(m.authorName) : undefined,
        authorId: m?.authorId ? String(m.authorId) : undefined,
        authorColor: m?.authorColor ?? null,
        createdAt: m?.createdAt ? String(m.createdAt) : undefined,
        attachments: Array.isArray(m?.attachments) ? m.attachments : [],
      }));
      setPreviewsByChan(old => ({ ...old, [fqChanId]: mapped }));
      setTimeout(() => {
        const el = favScrollRefs.current[fqChanId];
        try { if (el) el.scrollTop = el.scrollHeight; } catch {}
      }, 0);
    } catch {
      // mark as attempted to avoid spinner loops
      setPreviewsByChan(old => (Object.prototype.hasOwnProperty.call(old, fqChanId) ? old : { ...old, [fqChanId]: [] }));
    }
  }
  const refreshPreview = (fqChanId: string) => loadPreviewIfNeeded(fqChanId, true);

  // Quick reply text per favorite card (landing dashboard)
  const [quickTextByFav, setQuickTextByFav] = useState<Record<string, string>>({});
  function setQuick(fid: string, v: string) { setQuickTextByFav(old => ({ ...old, [fid]: v })); }
  const [quickPickerFor, setQuickPickerFor] = useState<string | null>(null);
  function addQuickEmoji(fid: string, emoji: string) {
    setQuick(fid, ((quickTextByFav[fid] || '') + emoji));
    setQuickPickerFor(null);
  }
  async function quickSend(fid: string) {
    const text = (quickTextByFav[fid] || '').trim();
    if (!text) return;
    const parts = fid.split(':');
    const vId = parts[0]; const cId = parts.slice(1).join(':') || 'general';
    const tempId = safeUuid();
    const fqid = fq(vId, cId);
    const now = Date.now();
    const lastTs = quickSendGuardRef.current[fqid] || 0;
    const lastText = quickSendLastTextRef.current[fqid] || '';
    if (now - lastTs < 1200 && lastText === text) {
      return; // prevent rapid duplicate submits of the same content
    }
    quickSendGuardRef.current[fqid] = now;
    quickSendLastTextRef.current[fqid] = text;
    setMsgsByKey(old => {
      const list = old[fqid] ?? [];
      return { ...old, [fqid]: [...list, { id: tempId, content: text, optimistic: true, createdAt: new Date().toISOString(), authorName: me.name || 'You' }] };
    });
    emitOrQueue({ voidId: vId, channelId: fq(vId, cId), content: text, tempId, attachments: [] });
    setQuickTextByFav(old => ({ ...old, [fid]: '' }));
  }
  // Favorites dashboard scroll refs for auto-scroll to newest
  const favScrollRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const landingGreeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);
  const landingDateLabel = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'short', day: 'numeric' }).format(new Date());
    } catch {
      return new Date().toDateString();
    }
  }, []);
  function linkifyMessage(text: string, keyPrefix: string) {
    return linkifyMessageForMembers(text, keyPrefix, channelVisibleMembers);
  }

  const keyboardInset = useKeyboardInsets();

  // Composer interactions (lifted for Composer component)
  const {
    gifOpen,
    setGifOpen,
    addOpen,
    setAddOpen,
    composerPickerOpen,
    setComposerPickerOpen,
    addBtnRef,
    addMenuRef,
    handleAddClick,
    handleAddTouch,
    handleOpenGif,
    addRecentEmoji,
  } = useMediaPickers();
  const handleCancelReply = () => setReplyTo(null);

  // composerHeight + isMdUp + the composer grow-effect moved to useResponsiveLayout (step 7.1).
  const {
    scrollToBottom, loadOlderMessages, hasMoreByChan, setHasMoreByChan, loadingMoreByChan, setLoadingMoreByChan,
    earliestMsgIdRef, isPrependingRef, skipAutoScrollRef,
  } = useMessagePaging({ currentVoidId, currentChannelId, token, stripSelfSeen, setMsgsByKey, listRef, keyboardInset });
  // Gallery selection & pagination state per channel
  // Per-channel gallery UI state (selection/pagination/image-loaded) lives in
  // useGalleryView (step 5.2c); consumed by the gallery JSX below.
  const {
    galSelModeByChan, setGalSelModeByChan,
    galSelectedByChan, setGalSelectedByChan,
    galVisibleCountByChan, setGalVisibleCountByChan,
    imgLoadedByUrl, setImgLoadedByUrl,
  } = useGalleryView();
  const toggleUserQuick = useCallback(() => setUserQuickOpen((prev) => !prev), []);
  const galleryFileRef = useRef<HTMLInputElement>(null);
  // Profile modal removed; avatar now navigates to landing
  const [userQuickOpen, setUserQuickOpen] = useState(false);
  const { userStatusMessage, updateUserStatusMessage } = useUserStatusMessage();
  // Per-channel shared-library item cache + its load effect live in useSharedItems
  // (step 6.3b). The derived currentSharedItems (below) reads the returned map.
  const { sharedItemsByChannel } = useSharedItems(currentVoidId, currentChannelId);
const isDmSpace = !!currentVoidId && String(currentVoidId).startsWith('dm_');

const channelVisibleMembers = useMemo(() => {
  if (isDmSpace) return members;
  if (!currentChannel || !Array.isArray(currentChannel.allowedRoles) || currentChannel.allowedRoles.length === 0) {
    return members;
  }
  const allowed = new Set((currentChannel.allowedRoles || []).map((id: any) => String(id)));
  return members.filter((m) => {
    if (String(m.role || '').toLowerCase() === 'owner') return true;
    if (Array.isArray(m.roles)) {
      return m.roles.some((r) => allowed.has(String((r as any).id)));
    }
    return false;
  });
}, [members, currentChannel, isDmSpace]);

const channelVisibleMembersWithPresence = useMemo(() => {
  return channelVisibleMembers.map((m) => {
    const statusRaw = (m as any).status || (m as any).rawStatus;
    const isInvisible = typeof statusRaw === "string" && statusRaw.toLowerCase() === "invisible";
    const onlinePresence =
      globalUserIds.includes(m.id) || spaceUserIds.includes(m.id) || roomUserIds.includes(m.id);
    const online = onlinePresence && !isInvisible;
    const onMobilePresence =
      globalMobileIds.includes(m.id) || spaceMobileIds.includes(m.id) || roomMobileIds.includes(m.id);
    const onMobile = onMobilePresence && !isInvisible;
    const isIdlePresence =
      globalIdleIds.includes(m.id) || spaceIdleIds.includes(m.id) || roomIdleIds.includes(m.id);
    const isIdle = online && isIdlePresence;
    return { ...m, online, onMobile, isIdle };
  });
}, [channelVisibleMembers, globalUserIds, spaceUserIds, roomUserIds, globalMobileIds, spaceMobileIds, roomMobileIds, globalIdleIds, spaceIdleIds, roomIdleIds]);

const onlineChannelMembers = useMemo(
  () => channelVisibleMembersWithPresence.filter((m) => m.online),
  [channelVisibleMembersWithPresence]
);
const offlineChannelMembers = useMemo(
  () => channelVisibleMembersWithPresence.filter((m) => !m.online),
  [channelVisibleMembersWithPresence]
);

useEffect(() => {
  if (isDmSpace && chanSheetOpen && channelPaneView !== 'dms') setChanSheetOpen(false);
}, [isDmSpace, chanSheetOpen, channelPaneView, setChanSheetOpen]);
useEffect(() => {
  // When the channel drawer is closed while showing the DMs list but the user is in a regular space,
  // reset the pane to 'spaces' so re-opening the drawer shows channels, not DMs.
  if (!chanSheetOpen && channelPaneView === 'dms' && !isDmSpace) setChannelPaneView('spaces');
// eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately keyed on the drawer closing only; reacting to channelPaneView would undo the user switching to the DMs list
}, [chanSheetOpen]);
  const headerSpaceLabel = currentSpace?.name || currentVoidId || 'Select space';
  const currentSpaceOnlineCount = useMemo(() => {
    if (!currentVoidId) return 0;
    const set = new Set(spaceUserIds.filter(Boolean));
    return set.size;
  }, [currentVoidId, spaceUserIds]);
  const composerPlaceholder = isDmSpace
    ? `Message ${currentSpace?.name || 'this conversation'}`
    : `Message #${currentChannel?.name ?? currentChannelId ?? 'general'}`;
  const dmPeer = useMemo(() => {
    if (!isDmSpace) return null;
    const meId = String(me?.userId || '');
    return (members || []).find((m: any) => String(m?.id || '') !== meId) || null;
  }, [isDmSpace, members, me?.userId]);
  const dmPeerId = dmPeer?.id ? String(dmPeer.id) : '';
  const dmPeerName = (dmPeer?.name || dmPeer?.username || '').trim();
  // Keep the members list (and the DM peer derived from it) in sync with the
  // active space/DM on EVERY navigation path. Previously members were only
  // refetched inside switchVoid, so reaching a DM another way (profile
  // "Message", invite/deep-link targets) left `members` pointing at the
  // previously viewed conversation — which made the header Call button dial the
  // wrong (previous) person. Clearing first means a stale peer is never dialed,
  // and the cancellation guard prevents a slow earlier fetch from clobbering a
  // newer space's members during rapid switches.
  useEffect(() => {
    if (!currentVoidId) { setMembers([]); return; }
    let cancelled = false;
    setMembers([]);
    (async () => {
      try {
        const tok = getAuthToken() || '';
        const res = await api.getAuth(`/spaces/members?spaceId=${encodeURIComponent(currentVoidId)}`, tok);
        if (!cancelled) setMembers(res.members || []);
      } catch { /* noop: presence sidebar simply stays empty until next load */ }
    })();
    return () => { cancelled = true; };
  }, [currentVoidId]);
  // Channels and members arrive over the socket / a fetch with no "done" flag: show skeleton rows briefly while still empty.
  const channelsLoading = useLoadingHold(channels.length === 0, currentVoidId && !currentVoidId.startsWith('dm_') ? currentVoidId : '', 1500);
  const peopleLoading = useLoadingHold(members.length === 0, currentVoidId, 1500);
  const currentSharedItems = currentChannelFqid ? (sharedItemsByChannel[currentChannelFqid] || []) : [];
  const chanDrawerProgress = (swipePanel === 'chan')
    ? (swipeMode === 'close' ? (1 - swipeProgress) : swipeProgress)
    : (chanSheetOpen ? 1 : 0);
  const usersDrawerProgress = (swipePanel === 'users')
    ? (swipeMode === 'close' ? (1 - swipeProgress) : swipeProgress)
    : (usersSheetOpen ? 1 : 0);

  // Offline send outbox (queue + persist + emitOrQueue) lives in useOutbox
  // (step 6.3a). The drop-on-ack (onNew) and reconnect flush (onConnect) stay in
  // the shared socket effect and call the returned setOutbox — those handlers are
  // the message/socket spine deferred to Phase 7 with the channels core (5.1c).
  const { setOutbox, emitOrQueue } = useOutbox(socket);
  const heartbeatRef = useRef<number | null>(null);
  // Removed: reaction picker refs (cleanup)

  // Removed: reaction picker outside/escape handling
  const [showJump, setShowJump] = useState(false);
  // sleek input modal (the prompt state lives in useInputModal); called here because the channel admin actions below use askInput
  const { inputOpen, inputCfg, askInput, closeInput } = useInputModal();
  // Move / rename / delete a channel (see useChannelAdminActions). Called before useQuickChannel, which receives moveChannelToFolder.
  const { moveChannelToFolder, renameChannelInline, deleteChannelInline } = useChannelAdminActions({ socket, currentVoidId, channels, askInput });

  // Quick channel-creation modal state + the three create actions live in
  // useQuickChannel (step 5.1e). channels/onChannelList stay in the parent
  // (5.1c deferred), so the optimistic-append setter and moveChannelToFolder
  // (hoisted function declaration) are passed in.
  const {
    quickChannelOpen,
    setQuickChannelOpen,
    quickChannelBusy,
    channelTypeOptions,
    quickAddChannel,
    quickAddChannelToFolder,
    handleQuickChannelCreate,
  } = useQuickChannel({ currentVoidId, isSpaceOwner, socket, setChannels, moveChannelToFolder });
  const sidebarChannels = useMemo(() => {
    const order = channelOrderForSpace;
    const ordered = [...channels].sort((a,b)=>{
      const ia = order.indexOf(a.id); const ib = order.indexOf(b.id);
      if (ia !== -1 || ib !== -1) return (ia===-1?1e9:ia) - (ib===-1?1e9:ib);
      const posDiff = Number(b.position || 0) - Number(a.position || 0);
      if (posDiff !== 0) return posDiff;
      return (a.name || '').localeCompare(b.name || '');
    });
    return ordered.filter((c) => canAccessChannel(c));
  }, [channels, channelOrderForSpace, canAccessChannel]);
  // Friends list/modal/search state lives in useFriends (extracted in step
  // 4.1). setFriendReqCount is wrapped in an arrow because useNotifications
  // is called further down (it needs friendsOpen/refreshFriends from here);
  // the wrapper only dereferences it when refreshFriends runs, post-render.
  const {
    friendsList,
    refreshFriends,
    friendsOpen,
    setFriendsOpen,
    dmFriendSearch,
    setDmFriendSearch,
    friendsOnline,
    filteredFriendsForDmSearch,
  } = useFriends({ globalUserIds, userId: me.userId, setFriendReqCount: (n) => setFriendReqCount(n) });
  const presenceFromStatus = useCallback((status?: string | null): 'online' | 'idle' | 'dnd' => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'dnd') return 'dnd';
    if (normalized === 'idle') return 'idle';
    return 'online';
  }, []);
  // switchChannel is re-created every render and reads current state; call the newest one.
  const switchChannelRef = useLatest(switchChannel);
  const handleSelectChannel = useCallback((chId: string) => {
    if (!currentVoidId) return;
    const cid = fq(currentVoidId, chId);
    setUnread(u=>({ ...u, [cid]: 0 }));
    setNotified(n=>{ const x={...n}; delete x[cid]; return x; });
    switchChannelRef.current(chId);
  }, [currentVoidId, setUnread, setNotified, switchChannelRef]);
  // Escape hatch for any unread that can't be cleared by opening a channel
  // (non-text channels, role-locked/hidden ones, or counts left by older app
  // versions). Advances every channel's read watermark server-side and wipes the
  // local badges; the server echoes unread:reset to the user's other devices.
  const markAllChannelsRead = useCallback(() => {
    try { socket.emit('read:all'); } catch { /* noop */ }
    setUnread({});
    setNotified({});
  }, [socket, setUnread, setNotified]);
  useEffect(() => {
    if (!currentVoidId || String(currentVoidId).startsWith('dm_')) return;
    if (sidebarChannels.length === 0) return;
    // While switching spaces the channel list briefly still holds the previous
    // space's channels (its channel:list hasn't arrived yet). Acting on that stale
    // list would flash us onto its first channel; wait until the list matches the
    // space we're now in.
    if (!sidebarChannels.every((c) => c.id.startsWith(`${currentVoidId}:`))) return;
    if (!sidebarChannels.some((c) => c.id === currentChannelId)) {
      // Prefer a non-voice channel so auto-selection never triggers the
      // "switch voice channel?" prompt while the user is on a call elsewhere
      const fallback = sidebarChannels.find((c) => c.type !== 'voice') || sidebarChannels[0];
      handleSelectChannel(fallback.id);
    }
  }, [currentVoidId, sidebarChannels, currentChannelId, handleSelectChannel]);

  // Drag-to-reorder for channels (desktop and the mobile Channels sheet) and for the mobile Spaces sheet: see useChannelReorder.
  const { handleChannelReorder } = useChannelReorder({ socket, currentVoidId, channels, setChanOrder });

  // Inline add for the homepage kanban favorite card: reads the typed text from
  // quickTextByFav[fid] (same per-card draft store the message composer uses),
  // posts it, and clears the bar so the user can keep typing cards back-to-back.
  async function quickAddKanbanCardInline(fqid: string, listId: string, fid: string) {
    const content = (quickTextByFav[fid] || '').trim();
    if (!content) return;
    setQuick(fid, '');
    try {
      const tok = getAuthToken() || '';
      await api.postAuth('/kanban/items', { listId, content }, tok);
      reloadKanban(fqid);
    } catch {
      setQuick(fid, content);
      toast('Failed to add card', 'error');
    }
  }
  const topOnlineFriends = useMemo(() => friendsOnline.slice(0, 6), [friendsOnline]);
  const mobileFriendSlots = useMemo(() => topOnlineFriends.slice(0, 5), [topOnlineFriends]);
  // Built after friendsList is known: the cards look friends' avatars up by user id.
  const landingFavoriteCards = buildLandingFavoriteCards({
    favorites, setFavorites, parseListFav, voids, getDmDisplayName, currentVoidId, channels, channelTypeById,
    kanbanByChan, setKanbanByChan, reloadKanban, forgetKanbanReload: (fqid) => { kanbanReloadedRef.current.delete(fqid); },
    msgsByKey, previewsByChan, unread, refreshPreview, quickTextByFav, setQuick, quickPickerFor, setQuickPickerFor,
    addQuickEmoji, quickSend, quickAddKanbanCardInline, favEmojis, favScrollRefs, showPeople,
    setChanSheetOpen, setUsersSheetOpen, switchVoid, switchChannel, k, fq,
    meId: me.userId ? String(me.userId) : undefined,
    avatarByUserId: (() => {
      const map: Record<string, string | null | undefined> = {};
      for (const f of friendsList) map[String(f.id)] = f.avatarUrl;
      for (const m of members) map[String(m.id)] = m.avatarUrl;
      if (me.userId) map[String(me.userId)] = me.avatarUrl;
      return map;
    })(),
    linkify: (text, key) => linkifyMessage(text, key).nodes,
  });
  const landingFavoritesCount = landingFavoriteCards.length;
  const landingSummaryStats = useMemo(() => ([
    { label: 'Favorites', value: landingFavoritesCount },
    { label: 'Spaces', value: voids.length },
    { label: 'Online now', value: friendsOnline.length },
  ]), [landingFavoritesCount, voids.length, friendsOnline.length]);
  const onlyWelcomeSpace = useMemo(() => {
    if (nonDmVoids.length !== 1) return false;
    const v = nonDmVoids[0];
    const name = String(v?.name || '').toLowerCase();
    const id = String(v?.id || '');
    return name === 'welcome' || id.startsWith('welcome-');
  }, [nonDmVoids]);
  // Notification-center state + the join-request/user-update/friends-update
  // socket handlers that feed it live in useNotifications (extracted in step
  // 3.3). refreshFriends stays parent-side (it also feeds friendsList, 4.1)
  // and reports the incoming-request badge through setFriendReqCount.
  const {
    notificationsOpen,
    setNotificationsOpen,
    unreadNotificationCount,
    notificationItems,
    markAllRead,
    removeNotification,
    friendReqCount,
    setFriendReqCount,
    notifySeenIdsRef,
  } = useNotifications({
    socket,
    token,
    friendsOpen,
    onOpenSettings,
    handleOpenProfile,
    refreshFriends,
  });

  // Landing "find a space" widget. The panel's search/request state lives
  // inside SpaceDiscoveryPanel (extracted in step 4.4); the welcome-only gate
  // stays here so the consumers' wrapper <div> only renders when the panel does.
  const landingSearchPanel = onlyWelcomeSpace ? (
    <SpaceDiscoveryPanel searchSpaces={searchSpaces} requestJoinSpace={requestJoinSpace} />
  ) : null;
  // Mute spaces/DMs: suppress notifications and prevent DM pop-ups
  const { mutedSpaces, setMutedSpaces } = useMutedSpaces(hiddenSpaces);
  // Voice chat state/logic lives in useVoiceCall (extracted in step 2.2b).
  const LIVEKIT_URL = (import.meta as any)?.env?.VITE_LIVEKIT_URL || '';
  const directCall = useDirectCallWiring({
    socket, token, userId: me?.userId, livekitUrl: LIVEKIT_URL, mutedSpaces, hiddenSpaces, answerCallRef, dismissCallRef,
  });
  const {
    voiceJoined,
    voiceMuted,
    voiceDeafened,
    voiceActiveChannelId,
    voiceActiveChannelName,
    voiceActiveSpaceId,
    voiceSwitchPrompt,
    setVoiceSwitchPrompt,
    voicePresenceByChannel,
    getVoiceNamesForChannel,
    viewingActiveCall,
    voiceRoomParticipants,
    voiceVolumes,
    voiceSilenced,
    videoEnabled,
    videoBusy,
    devicesOpen,
    toggleDevices,
    inputs,
    outputs,
    selectedInputId,
    setSelectedInputId,
    selectedOutputId,
    setSelectedOutputId,
    needsAudioUnlock,
    setNeedsAudioUnlock,
    voiceAudioRoute,
    setVoiceAudioRoute,
    voiceAvailableRoutes,
    remoteStreamsRef,
    audioElsRef,
    liveKitRoomRef,
    joinVoice,
    leaveVoice,
    confirmVoiceSwitch,
    toggleMute,
    toggleDeafen,
    toggleVideo,
    switchMicrophone,
    applyOutputDevice,
    refreshDevices,
    unlockRemoteAudio,
    setParticipantVolume,
  } = useVoiceCall({
    socket,
    token,
    me,
    user,
    members,
    channels,
    currentVoidId,
    currentChannelId,
    livekitUrl: LIVEKIT_URL,
    // Space/channel switching and pending-switch bookkeeping stay parent-side;
    // confirmVoiceSwitch navigates through this callback.
    navigateToVoiceChannel: (spaceId, channelId) => {
      if (spaceId !== currentVoidId) {
        switchVoid(spaceId);
        pendingChannelSwitchRef.current = { voidId: spaceId, channelId };
        setCurrentChannelId(channelId);
      } else {
        switchChannel(channelId);
      }
    },
  });

  // Keep the voiceJoined ? ... : null decision here: ChannelSidebar shows its
  // footer wrapper whenever `footer` is truthy, and a JSX element is truthy
  // even when the component would render null.
  const voiceDock = voiceJoined ? (
    <VoiceDock
      voiceActiveChannelId={voiceActiveChannelId}
      voiceActiveChannelName={voiceActiveChannelName}
      voiceActiveSpaceId={voiceActiveSpaceId}
      currentVoidId={currentVoidId}
      activeSpaceName={voids.find((v) => v.id === voiceActiveSpaceId)?.name || voiceActiveSpaceId}
      voiceMuted={voiceMuted}
      videoEnabled={videoEnabled}
      videoBusy={videoBusy}
      devicesOpen={devicesOpen}
      // Navigation stays parent-side (tangle point 3)
      onJumpToCall={() => {
        if (voiceActiveSpaceId && voiceActiveSpaceId !== currentVoidId) switchVoid(voiceActiveSpaceId);
        if (voiceActiveChannelId) handleSelectChannel(voiceActiveChannelId);
      }}
      toggleMute={toggleMute}
      toggleVideo={toggleVideo}
      toggleDevices={toggleDevices}
      refreshDevices={refreshDevices}
      leaveVoice={leaveVoice}
    />
  ) : null;


  // Connects the socket and runs the on-connect work (unread refresh, presence ping, outbox flush, deep link, room re-join): see useSocketLifecycle.
  useSocketLifecycle({ socket, token, onTokenChange, connect, disconnect, currentVoidIdRef, currentChannelIdRef, channelsRef, pendingReadRef, heartbeatRef, openChannelFromFqidRef, setUnread, setOutbox, setDmPageOpen, setMembers, loadKanbanIfNeeded, loadFormIfNeeded });

  // Small socket handlers about the signed-in user and alerts (registered below with the rest): see useAccountSocketHandlers.
  const accountSocketHandlers = useAccountSocketHandlers({ socket, userId: me.userId, currentVoidId, currentChannelId, currentVoidIdRef, channels, mutedSpaces, hiddenSpaces, favorites, setMe, setUnread, setNotified, setMembers, handleNotification, addPreview, refreshPreview, markUnread: markUnreadTracked, openChannelFromFqid, notifiedRef, seenIdsRef: notifySeenIdsRef });

  // Handlers for messages already in the list (read receipt, edit, delete, reactions, backlog): see useMessageSocketHandlers.
  const messageSocketHandlers = useMessageSocketHandlers({ currentVoidId, currentChannelId, currentVoidIdRef, currentChannelIdRef, socket, setMsgsByKey, stripSelfSeen, editingId, setEditingId, setEditText, pageSize: MESSAGE_PAGE_SIZE, earliestMsgIdRef, setHasMoreByChan, setLoadingMoreByChan, setUnread, setNotified, readReceiptsEnabledRef, selfInvisibleRef });

  // A new message arriving (or the server acknowledging ours): add it, notify, mark read, scroll, ...: see useIncomingMessageHandlers.
  const incomingMessageHandlers = useIncomingMessageHandlers({ currentVoidId, currentChannelId, userId: me.userId, channels, mutedSpaces, hiddenSpaces, favorites, recentIncomingRef, listRef, favScrollRefs, notifiedRef, seenIdsRef: notifySeenIdsRef, setMsgsByKey, setChatInContextMode, setOutbox, setUnreadMarkerByChan, setNotified, stripSelfSeen, markUnreadTracked, markChannelRead, scrollToBottom, handleNotification, addPreview, refreshPreview, openChannelFromFqid });

  useChatSocketRegistration({
    socket, selfUserId: me.userId, currentChannelId, currentVoidIdRef, accountSocketHandlers, messageSocketHandlers, incomingMessageHandlers,
    channelTypeById, setChannels, setLockedChannelMap, setChannelTypeById, setChanOrder, setMsgsByKey, pendingChannelSwitchRef,
    switchChannel, loadKanbanIfNeeded, loadFormIfNeeded, setKanbanByChan, setChannelTagsByChan, setFormByChan, scheduleFormReload,
  });

    // Auto-scroll for text-like channels (no type or type === 'text'/'dm') on load, channel switch, and new messages
    useLayoutEffect(() => {
      const meta = channels.find((c) => c.id === currentChannelId);
      const t = meta?.type;
      const isTextLike = !t || t === "text" || t === "dm";
      if (!isTextLike) return;
      if (isPrependingRef.current || skipAutoScrollRef.current) return;
      // Double RAF to ensure after paint
      requestAnimationFrame(() => {
        scrollToBottom();
      requestAnimationFrame(() => scrollToBottom());
    });
  }, [msgs.length, currentVoidId, currentChannelId, channels, scrollToBottom]);

  // On initial load/refresh, nudge to bottom once messages render
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (isPrependingRef.current || skipAutoScrollRef.current) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, []);

  // Ensure we jump to newest when opening a chat/text channel even if message count hasn't changed
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (isPrependingRef.current || skipAutoScrollRef.current) return;
    const meta = channels.find(c => c.id === currentChannelId);
    if (meta?.type === 'voice') {
      requestAnimationFrame(() => {
        el.scrollTop = 0;
        requestAnimationFrame(() => { el.scrollTop = 0; });
      });
      return;
    }
    if (meta && (!meta.type || meta.type === 'text' || meta.type === 'dm' || meta.type === 'announcement')) {
      // Scroll to bottom to show newest messages; use RAF to ensure after paint
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
      });
    }
  }, [currentVoidId, currentChannelId, channels]);

  useEffect(() => {
    if (!currentVoidId || !currentChannelId) return;
    notifyNativeChannelViewed(currentVoidId, currentChannelId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the viewed channel changes; notifyNativeChannelViewed is a pure helper re-created each render
  }, [currentVoidId, currentChannelId]);

  // Feeds the Android launcher's recent-channel shortcuts. Kept separate from
  // notifyNativeChannelViewed because that one clears notifications and must fire
  // on the id alone, while a shortcut needs display names — which arrive with the
  // channel list, sometimes a beat after the switch. Depending on channels/voids
  // means a late list (or a later rename) re-pushes the corrected label.
  useEffect(() => {
    if (!currentVoidId || !currentChannelId) return;
    const bridge = (window as any)?.EchoAndroid;
    if (!bridge || typeof bridge.pushChannelShortcut !== "function") return;
    const name = channels.find((c) => c.id === currentChannelId)?.name;
    if (!name) return;
    const spaceName = voids.find((v) => v.id === currentVoidId)?.name || "";
    try {
      bridge.pushChannelShortcut(currentVoidId, fq(currentVoidId, currentChannelId), name, spaceName);
    } catch {
      // ignore native bridge errors
    }
  }, [currentVoidId, currentChannelId, channels, voids]);

	  // Mobile UX: show "Jump to present" when scrolled up; clear unread divider when near bottom
	  useEffect(() => {
	    if (!isMessageChannelType) {
	      setShowJump(false);
	      return;
	    }
	    const el = listRef.current;
	    if (!el) return;
    const onScroll = () => {
      const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 40;
      setShowJump(!nearBottom);
      if (nearBottom) {
        const cid = fq(currentVoidId, currentChannelId);
        setUnreadMarkerByChan(prev => ({ ...prev, [cid]: null }));
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
	  }, [currentVoidId, currentChannelId, isMessageChannelType, setUnreadMarkerByChan]);

  // Autoscroll favorites widgets to show most recent content
  useEffect(() => {
    try {
      for (const fid of favorites.slice(0, 4)) {
        const el = favScrollRefs.current[fid];
        if (el) el.scrollTop = el.scrollHeight;
      }
    } catch { /* noop */ }
  }, [favorites, msgsByKey, previewsByChan, kanbanByChan]);

  // actions
  const {
    onInputChange, insertEmojiAtCaret, send, onFilesSelected, addRemoteAsUpload, isMine, startEdit,
    addGalleryFiles, saveEdit, cancelEdit, handleDeleteMessage, toggleReaction,
  } = useMessageActions({
    socket, currentVoidId, currentChannelId, userId: me.userId, text, setText, inputRef, spoiler, setSpoiler, replyTo, setReplyTo,
    editingId, setEditingId, editText, setEditText, setPickerFor, uploading, pendingUploads, addUploads, clearPendingUploads,
    setPendingUploads, setMsgsByKey, emitOrQueue, refreshPreview, galleryFileRef,
  });

  // Quick reaction bar: prefer user-configured reactions (Appearance settings), then legacy favEmojis, then recents


  async function handleChannelAccessUpdate(channelId: string, roleIds: string[]) {
    if (!currentVoidId) return;
    try {
      const tok = getAuthToken() || '';
      await api.postAuth('/channels/access', { spaceId: currentVoidId, channelId, roleIds }, tok);
      const idVariants = channelId.includes(':')
        ? [channelId, channelId.split(':').slice(1).join(':')]
        : [channelId, `${currentVoidId}:${channelId}`];
      setChannels((prev) =>
        prev.map((c) => (idVariants.includes(c.id) ? { ...c, allowedRoles: [...roleIds] } : c))
      );
      socket.emit("channel:list", { voidId: currentVoidId });
    } catch (e:any) {
      toast(e?.message || 'Failed to update channel access', 'error');
    }
  }

  async function handleChannelLockToggle(ch: Channel) {
    if (!currentVoidId) return;
    try {
      const tok = getAuthToken() || '';
      if (ch.locked) {
        const ok = window.confirm('Remove the passcode from this channel?');
        if (!ok) return;
        await api.postAuth('/channels/lock/clear', { spaceId: currentVoidId, channelId: ch.id }, tok);
      } else {
        const raw = await askInput({ title: 'Lock channel', label: 'Set a passcode (min 4 characters)', placeholder: 'Passcode', okText: 'Lock' });
        if (raw == null) return;
        const pass = String(raw).trim();
        if (pass.length < 4) { toast('Passcode must be at least 4 characters', 'error'); return; }
        await api.postAuth('/channels/lock', { spaceId: currentVoidId, channelId: ch.id, passcode: pass }, tok);
      }
      socket.emit("channel:list", { voidId: currentVoidId });
    } catch (e:any) {
      toast(e?.message || 'Failed to update lock', 'error');
    }
  }

  const appendComposerText = useCallback((t: string) => {
    setText((prev) => (prev ? `${prev}\n${t}` : t));
  }, [setText]);
  const androidShare = useAndroidShare({
    currentVoidId,
    currentChannelId,
    voids,
    channels,
    appendText: appendComposerText,
    addUploads,
  });

  // Paste images directly into the composer (and the Android keyboard GIF fallback): see useComposerPaste.
  const { onComposerPaste } = useComposerPaste({ setPendingUploads });





  const { createNote, updateNote, deleteNote } = useNotesActions({ socket, currentVoidId, currentChannelId, setMsgsByKey, emitOrQueue });

  // Warms the data behind favourite cards (kanban lists, channel previews, one retry for an empty list): see useFavoritePreloads.
  const { kanbanReloadedRef } = useFavoritePreloads({ favorites, parseListFav, kanbanByChan, previewsByChan, msgsByKey, loadKanbanIfNeeded, reloadKanban, loadPreviewIfNeeded });



  const { getLastChannelBySpace, rememberLastChannel } = useLastChannelBySpace();
  const navigation = useChannelNavigation({
    socket, currentVoidId, currentVoidIdRef, channelPaneView, setChannelPaneView, setCurrentVoidId, setCurrentChannelId,
    onDismissProfileRoute, onDismissSettingsRoute, voids, channels, isDmSpace, canAccessChannel, setChannelFolders,
    setUnread, setNotified, setTypers, setRoomUserIds, markChannelRead, pendingChannelSwitchRef, maybePromptChannelUnlock,
    liveKitRoomRef, voiceActiveChannelId, voiceActiveSpaceId, setVoiceSwitchPrompt, loadKanbanIfNeeded, loadFormIfNeeded,
    getLastChannelBySpace, rememberLastChannel, notifyNativeChannelViewed,
  });
  navigationRef.current = navigation;

  const { setDefaultChannel } = useDefaultChannel({ currentVoidId, setVoids });


  useEffect(() => {
    if (!currentChannelFqid || !currentChannelId) return;
    const meta = channels.find(c => c.id === currentChannelId);
    if (!meta) return;
    if (!['kanban', 'form', 'notes'].includes(String(meta.type || '').toLowerCase())) return;
    fetchChannelTags(currentChannelFqid);
  }, [currentChannelFqid, currentChannelId, channels, fetchChannelTags]);

  // While viewing a form channel, periodically refresh answers to keep everyone in sync.
  // The timer outlives renders, so it calls the newest reloadForm through a ref.
  const reloadFormRef = useLatest(reloadForm);
  useEffect(() => {
    try {
      const meta = channels.find(c => c.id === currentChannelId);
      if (!meta || meta.type !== 'form') return;
      const fid = fq(currentVoidId, currentChannelId);
      // immediate refresh on entering
      reloadFormRef.current(fid);
      const t = window.setInterval(() => { reloadFormRef.current(fid); }, 5000);
      return () => window.clearInterval(t);
    } catch { /* noop */ }
  }, [currentVoidId, currentChannelId, channels, reloadFormRef]);

  // --- Minimal UI ---
  return (
    <>
    <div
      className="h-app w-full flex flex-col sm:flex-row brand-app-bg text-fg overflow-hidden"
      style={{ paddingTop: (window as any).echoDesktop ? DESKTOP_TITLE_BAR_HEIGHT : 0 }}
    >
      <DesktopTitleBar
        title={[currentChannel?.name && `# ${currentChannel.name}`, voids.find((v) => v.id === currentVoidId)?.name].filter(Boolean).join(' · ') || 'Echo'}
      />
      <DesktopSidebars
        voiceJoined={voiceJoined}
        voids={voids}
        hiddenSpaces={hiddenSpaces}
        spaceOrder={spaceOrder}
        setSpaceOrder={setSpaceOrder}
        unread={unread}
        currentVoidId={currentVoidId}
        me={me}
        user={user}
        userQuickOpen={userQuickOpen}
        friendReqCount={friendReqCount}
        unreadNotificationCount={unreadNotificationCount}
        members={members}
        channelsLoading={channelsLoading}
        peopleLoading={peopleLoading}
        currentChannelId={currentChannelId}
        dmPageOpen={dmPageOpen}
        channelPaneView={channelPaneView}
        setChanSheetOpen={setChanSheetOpen}
        setUsersSheetOpen={setUsersSheetOpen}
        setChannelPaneView={setChannelPaneView}
        setCurrentVoidId={setCurrentVoidId}
        setCurrentChannelId={setCurrentChannelId}
        setDmPageOpen={setDmPageOpen}
        setDmSearch={setDmSearch}
        setDmSearchOpen={setDmSearchOpen}
        setNotificationsOpen={setNotificationsOpen}
        setFriendsOpen={setFriendsOpen}
        switchVoid={switchVoid}
        switchChannel={switchChannel}
        toggleUserQuick={toggleUserQuick}
        createSpace={createSpace}
        showPeople={showPeople}
        peopleW={peopleW}
        isWideUp={isWideUp}
        startDrag={startDrag}
        onlineChannelMembers={onlineChannelMembers}
        offlineChannelMembers={offlineChannelMembers}
        onOpenProfile={onOpenProfile}
        peoplePanelCollapsed={peoplePanelCollapsed}
        setPeoplePanelCollapsed={setPeoplePanelCollapsed}
        isDmSpace={isDmSpace}
        chanW={chanW}
        isSpaceOwner={isSpaceOwner}
        sidebarChannels={sidebarChannels}
        channelFolders={channelFolders}
        chanOrder={chanOrder}
        voicePresenceByChannel={voicePresenceByChannel}
        quickAddChannel={quickAddChannel}
        createFolderInline={createFolderInline}
        askInput={askInput}
        quickAddChannelToFolder={quickAddChannelToFolder}
        toggleFav={toggleFav}
        isFav={isFav}
        moveChannelToFolder={moveChannelToFolder}
        renameChannelInline={renameChannelInline}
        deleteChannelInline={deleteChannelInline}
        handleChannelReorder={handleChannelReorder}
        handleFolderReorder={handleFolderReorder}
        spaceRoles={spaceRoles}
        handleChannelAccessUpdate={handleChannelAccessUpdate}
        handleChannelLockToggle={handleChannelLockToggle}
        setDefaultChannel={setDefaultChannel}
        voiceDock={voiceDock}
        handleSelectChannel={handleSelectChannel}
        dmSearch={dmSearch}
        dmSearchOpen={dmSearchOpen}
        friendsOnline={friendsOnline}
        friendsList={friendsList}
        dmStartBusyUserId={dmStartBusyUserId}
        activeDmVoids={activeDmVoids}
        msgsByKey={msgsByKey}
        notified={notified}
        tabBgClass={tabBgClass}
        getDmDisplayName={getDmDisplayName}
        startDmWithUser={startDmWithUser}
      />

      {/* Main column — raised-card surface, rounded on all four corners; md:overflow-hidden clips square child headers to the corners */}
      <div role="main" className="flex-1 flex flex-col min-h-0 pb-0 bg-panel-main panel-card md:rounded-2xl md:overflow-hidden">
        {/* Channel / DM header */}
        {currentVoidId ? (
          <ChannelHeader
            isDmSpace={isDmSpace}
            dmPeer={dmPeer}
            dmPeerName={dmPeerName}
            onOpenPeerProfile={() => dmPeerId && handleOpenProfile(dmPeerId)}
            voiceCall={{ phase: directCall.phase, canCall: directCall.canCall, busy: directCall.busy, livekitConfigured: !!LIVEKIT_URL, onClick: () => { if (directCall.phase === 'idle') void directCall.startCall(dmPeerId, dmPeerName || 'Direct contact'); else if (directCall.phase === 'outgoing') void directCall.cancelCall(); else if (directCall.phase === 'in_call') void directCall.endCall(); } }}
            onAddPerson={() => setFriendsOpen(true)}
            isFavorite={isFav(currentVoidId, 'chat')}
            onToggleFavorite={() => toggleFav(currentVoidId, 'chat')}
            currentChannel={currentChannel}
            currentChannelId={currentChannelId}
            currentSpaceOnlineCount={currentSpaceOnlineCount}
            onShowOnline={() => { setUsersSheetOpen(true); setChanSheetOpen(false); }}
            searchRef={chatSearchRef}
            searchOpen={chatSearchOpen}
            searchQuery={chatSearchQuery}
            setSearchQuery={setChatSearchQuery}
            searchFilter={chatSearchFilter}
            searchFromUser={chatSearchFromUser}
            searchLoading={chatSearchLoading}
            searchResults={chatSearchResults}
            onToggleSearch={() => { setChatSearchOpen(o => !o); setChatSearchQuery(''); setChatSearchFilter('all'); setChatSearchFromUser(''); }}
            onClearSearch={() => { setChatSearchQuery(''); setChatSearchFilter('all'); setChatSearchFromUser(''); }}
            onClearFilter={() => { setChatSearchFilter('all'); setChatSearchFromUser(''); }}
            onSelectFilter={(f) => { setChatSearchFilter(f); }}
            onOpenSearchResult={(messageId) => {
              const key = currentChannelFqid
                || (currentChannelId?.includes(':') ? currentChannelId : `${currentVoidId}:${currentChannelId}`)
                || '';
              const tok = getAuthToken() || '';
              setChatSearchOpen(false);
              api.getAuth(
                `/channels/context?channelId=${encodeURIComponent(key)}&messageId=${encodeURIComponent(messageId)}`,
                tok
              ).then((res: any) => {
                const contextMsgs: Msg[] = Array.isArray(res?.messages) ? res.messages : [];
                if (contextMsgs.length > 0) {
                  setMsgsByKey(prev => ({ ...prev, [key]: contextMsgs }));
                  setChatInContextMode(true);
                }
                setChatHighlightId(messageId);
                setTimeout(() => {
                  setChatScrollToId(messageId);
                  setTimeout(() => setChatHighlightId(null), 3000);
                }, 150);
              }).catch(() => {
                setChatScrollToId(messageId);
              });
            }}
          />
        ) : null}
        {currentVoidId ? (
          /* Spacer for the fixed channel/DM header — matches its height */
          <div className="sm:hidden" style={{ height: isDmSpace ? 48 : 40 }} />
        ) : null}

        {/* Removed top channel bar; channels now in left column */}

        {/* Messages, Kanban, or landing */}
        {currentSharedItems.length > 0 && currentChannel?.type !== "voice" && currentChannel?.type !== "kitchen" && (
          <div className="px-3 sm:px-4 pb-2">
            <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
              <div className="flex items-center justify-between text-meta text-fg-3">
                <span>Shared Library</span>
                <span>{currentSharedItems.length} item{currentSharedItems.length === 1 ? '' : 's'}</span>
              </div>
              <div className="mt-2">
                <SharedItemsShelf
                  items={currentSharedItems}
                  meUserId={me?.userId || null}
                  renderPreview={renderPersonalItemPreview}
                />
              </div>
            </div>
          </div>
        )}
        <div
          ref={listRef}
        className={`${currentVoidId
          ? 'overflow-y-auto overflow-x-hidden pt-4 pb-0 md:pt-4 sm:pt-4 sm:pb-0 md:pb-0'
          : 'overflow-y-auto overflow-x-hidden pt-0 pb-0 min-h-screen'} overscroll-contain w-full min-w-0 px-4 space-y-2 bg-transparent sm:px-6 flex-1`}
        style={{
          paddingBottom: isMdUp ? 12 : Math.max(12, composerHeight + 1) + (!isMdUp && voiceJoined ? 68 : 0),
        }}
        >
          {currentVoidId && hasMoreByChan[fq(currentVoidId, currentChannelId)] && (
            <div className="flex justify-center pt-2">
              <button
                className="rounded-full border border-white/10 bg-neutral-900/80 px-3 py-1 text-xs text-fg hover:border-emerald-500/50 hover:text-white disabled:opacity-50"
                onClick={loadOlderMessages}
                disabled={!!loadingMoreByChan[fq(currentVoidId, currentChannelId)]}
              >
                {loadingMoreByChan[fq(currentVoidId, currentChannelId)] ? 'Loading older messages…' : 'Load previous messages'}
              </button>
            </div>
          )}
          {currentVoidId && currentChannel?.type === "voice" && (
            <div className="mx-auto flex h-full w-full max-w-5xl flex-col px-2 pb-6 pt-2 sm:px-4">
              <div className="flex items-center justify-between gap-3 px-2 text-xs text-fg-3">
                <span className="inline-flex items-center gap-2">
                  <span className={`h-1.5 w-1.5 rounded-full ${viewingActiveCall ? "bg-emerald-400" : "bg-white/25"}`} />
                  {viewingActiveCall ? "Live voice" : "Voice room"}
                </span>
                <span>{voiceRoomParticipants.length} online</span>
              </div>
              {!LIVEKIT_URL ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-neutral-700/40 via-neutral-800/20 to-transparent flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 text-fg-3"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/><line x1="2" y1="2" x2="22" y2="22" strokeWidth="1.5"/></svg>
                  </div>
                  <div className="text-base font-medium text-fg-3">Voice not available</div>
                  <div className="max-w-xs text-sm text-fg-3">Voice channels require a LiveKit server. Contact the server administrator to enable voice.</div>
                </div>
              ) : voiceRoomParticipants.length > 0 ? (
                <div className="flex flex-1 flex-wrap content-center justify-center gap-5 py-8 sm:gap-8">
                  {voiceRoomParticipants.map((p) => (
                    <VoiceTile key={p.id} participant={p} volumeFor={(id) => voiceVolumes[id] ?? 1} onVolumeChange={setParticipantVolume} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
                  <div className="h-16 w-16 rounded-full bg-white/[0.06]" />
                  <div className="text-base font-medium text-fg-2">Quiet in here</div>
                  <div className="max-w-xs text-sm text-fg-3">Join voice when you are ready to start the room.</div>
                </div>
              )}
            </div>
          )}
          <ChannelBody
            currentVoidId={currentVoidId}
            currentChannelId={currentChannelId}
            currentChannelType={currentChannelType}
            currentChannel={currentChannel}
            currentChannelFqid={currentChannelFqid}
            fq={fq}
            msgs={msgs}
            messagesLoading={messagesLoading}
            channels={channels}
            setChannels={setChannels}
            members={members}
            me={me}
            user={user}
            listRef={listRef}
            isMdUp={isMdUp}
            channelPaneView={channelPaneView}
            activeDmVoids={activeDmVoids}
            dmFriendSearch={dmFriendSearch}
            setDmFriendSearch={setDmFriendSearch}
            filteredFriendsForDmSearch={filteredFriendsForDmSearch}
            dmStartBusyUserId={dmStartBusyUserId}
            unread={unread}
            getDmDisplayName={getDmDisplayName}
            landingDateLabel={landingDateLabel}
            landingGreeting={landingGreeting}
            userStatusMessage={userStatusMessage}
            onUpdateStatus={updateUserStatusMessage}
            friendReqCount={friendReqCount}
            landingSummaryStats={landingSummaryStats}
            landingSearchPanel={landingSearchPanel}
            friendsOnline={friendsOnline}
            mobileFriendSlots={mobileFriendSlots}
            globalUserIds={globalUserIds}
            presenceFromStatus={presenceFromStatus}
            nonDmVoids={nonDmVoids}
            landingFavoriteCards={landingFavoriteCards}
            startDmWithUser={startDmWithUser}
            switchVoid={switchVoid}
            switchChannel={switchChannel}
            handleOpenProfile={handleOpenProfile}
            setFriendsOpen={setFriendsOpen}
            createSpace={createSpace}
            currentSharedItems={currentSharedItems}
            onOpenSettings={onOpenSettings}
            askInput={askInput}
            currentKanbanFqid={currentKanbanFqid}
            currentKanbanLists={currentKanbanLists}
            currentKanbanTags={currentKanbanTags}
            currentChannelTags={currentChannelTags}
            mutateKanbanLists={mutateKanbanLists}
            mutateChannelTags={mutateChannelTags}
            fetchChannelTags={fetchChannelTags}
            isListFav={isListFav}
            toggleListFav={toggleListFav}
            createNote={createNote}
            updateNote={updateNote}
            deleteNote={deleteNote}
            galSelModeByChan={galSelModeByChan}
            setGalSelModeByChan={setGalSelModeByChan}
            galSelectedByChan={galSelectedByChan}
            setGalSelectedByChan={setGalSelectedByChan}
            galVisibleCountByChan={galVisibleCountByChan}
            setGalVisibleCountByChan={setGalVisibleCountByChan}
            imgLoadedByUrl={imgLoadedByUrl}
            setImgLoadedByUrl={setImgLoadedByUrl}
            galleryFileRef={galleryFileRef}
            addGalleryFiles={addGalleryFiles}
            setImagePreview={setImagePreview}
            setImagePreviewList={setImagePreviewList}
            setImagePreviewIndex={setImagePreviewIndex}
            setVideoPreview={setVideoPreview}
            unreadMarkerByChan={unreadMarkerByChan}
            hasMoreByChan={hasMoreByChan}
            isMine={isMine}
            pickerFor={pickerFor}
            setPickerFor={setPickerFor}
            reactionBtnRef={reactionBtnRef}
            reactionMenuRef={reactionMenuRef}
            setReplyTo={setReplyTo}
            startEdit={startEdit}
            editingId={editingId}
            editText={editText}
            setEditText={setEditText}
            saveEdit={saveEdit}
            cancelEdit={cancelEdit}
            toggleReaction={toggleReaction}
            revealedSpoilers={revealedSpoilers}
            setRevealedSpoilers={setRevealedSpoilers}
            linkifyMessage={linkifyMessage}
            linkPreviewMap={linkPreviewMap}
            renderLinkPreview={renderLinkPreview}
            hoverReaction={hoverReaction}
            setHoverReaction={setHoverReaction}
            reactionTipPos={reactionTipPos}
            setReactionTipPos={setReactionTipPos}
            reactionInfoByMsg={reactionInfoByMsg}
            loadReactionsFor={loadReactionsFor}
            seenTip={seenTip}
            setSeenTip={setSeenTip}
            timeAgo={timeAgo}
            formatTime={formatTime}
            handleDeleteMessage={handleDeleteMessage}
            getQuickEmojis={getQuickEmojis}
            chatScrollToId={chatScrollToId}
            chatHighlightId={chatHighlightId}
          />
        </div>

        {/* Jump to latest — shown when viewing a historical context window */}
        {chatInContextMode && (
          <JumpToLatestButton
            tabBgClass={tabBgClass}
            onJump={() => {
              const key = currentChannelFqid
                || (currentChannelId?.includes(':') ? currentChannelId : `${currentVoidId}:${currentChannelId}`)
                || '';
              if (!key) return;
              const tok = getAuthToken() || '';
              api.getAuth(`/channels/history?channelId=${encodeURIComponent(key)}&limit=30`, tok)
                .then((res: any) => {
                  const latest: Msg[] = Array.isArray(res?.messages) ? res.messages : [];
                  if (latest.length > 0) setMsgsByKey(prev => ({ ...prev, [key]: latest }));
                  setChatInContextMode(false);
                  setTimeout(() => scrollToBottom(), 80);
                })
                .catch(() => { setChatInContextMode(false); scrollToBottom(); });
            }}
          />
        )}

        {/* Typing + presence (sticky above composer on mobile) */}
        <TypingIndicator
          currentVoidId={currentVoidId}
          typers={typers}
          isMdUp={isMdUp}
          keyboardInset={keyboardInset}
          composerHeight={composerHeight}
        />

        {/* Voice room or Input (hidden for Kanban/Questions/Task) */}
        {currentChannelType !== 'kanban' && currentChannelType !== 'form' && currentChannelType !== 'habit' && currentChannelType !== 'gallery' && currentChannelType !== 'notes' && currentChannelType !== 'kitchen' && currentChannelType !== 'calendar' && (
          <div className="p-3 bg-transparent" style={{ backdropFilter: "none", WebkitBackdropFilter: "none" }}>
            {!currentVoidId ? null : currentChannelType === 'voice' ? (
              <VoiceChannelView
                voiceJoined={voiceJoined && voiceActiveChannelId === currentChannelId && voiceActiveSpaceId === currentVoidId}
                voiceMuted={voiceMuted}
                voiceAvailable={!!LIVEKIT_URL}
                channelName={currentChannel?.name || currentChannelId}
                participantCount={voiceRoomParticipants.length}
                devicesOpen={devicesOpen}
                selectedInputId={selectedInputId}
                selectedOutputId={selectedOutputId}
                inputs={inputs}
                outputs={outputs}
                needsAudioUnlock={needsAudioUnlock}
                voiceVolumes={voiceVolumes}
                voiceSilenced={voiceSilenced}
                voiceDeafened={voiceDeafened}
                remoteStreams={remoteStreamsRef.current}
                toggleMute={toggleMute}
                toggleDeafen={toggleDeafen}
                joinVoice={joinVoice}
                leaveVoice={leaveVoice}
                toggleDevices={toggleDevices}
                refreshDevices={refreshDevices}
                toggleVideo={toggleVideo}
                videoEnabled={videoEnabled}
                videoBusy={videoBusy}
                switchMicrophone={switchMicrophone}
                applyOutputDevice={applyOutputDevice}
                unlockAudio={unlockRemoteAudio}
                audioElsRef={audioElsRef}
                setNeedsAudioUnlock={setNeedsAudioUnlock}
                setSelectedInputId={setSelectedInputId}
                setSelectedOutputId={setSelectedOutputId}
                audioRoute={voiceAudioRoute}
                availableRoutes={voiceAvailableRoutes}
                onChangeAudioRoute={(route) => {
                  setAudioRoute(route);
                  setVoiceAudioRoute(route);
                }}
              />
            ) : (!currentVoidId || !currentChannel || currentChannel.type === 'form' || currentChannel.type === 'gallery' || currentChannel.type === 'challenge') ? null : (
              <div
                style={{
                  paddingBottom: `calc(env(safe-area-inset-bottom, 0px))`,
                }}
              >
                <Composer
                  pendingUploads={pendingUploads}
                  onRemoveUpload={handleRemovePending}
                  addOpen={addOpen}
                  onToggleAdd={handleAddClick}
                  onTouchToggleAdd={handleAddTouch}
                  addBtnRef={addBtnRef}
                  addMenuRef={addMenuRef}
                  uploading={uploading}
                  uploadError={uploadError || undefined}
                  spoiler={spoiler}
                  onToggleSpoiler={() => setSpoiler((v) => !v)}
                  onOpenGif={handleOpenGif}
                  onFilesSelected={(files) => { onFilesSelected(files); setAddOpen(false); }}
                  replyTo={replyTo}
                  onCancelReply={handleCancelReply}
                  inputRef={inputRef}
                  composerPlaceholder={composerPlaceholder}
                  text={text}
                  onInputChange={onInputChange}
                  mentionMembers={channelVisibleMembers}
                  onComposerPaste={onComposerPaste}
                  onSend={send}
                  composerPickerOpen={composerPickerOpen}
                  onOpenPicker={() => setComposerPickerOpen(true)}
                  onClosePicker={() => setComposerPickerOpen(false)}
                  addRecentEmoji={addRecentEmoji}
                  insertEmojiAtCaret={insertEmojiAtCaret}
                  quickEmojis={getQuickEmojis()}
                  keyboardInset={keyboardInset}
                  onHeightChange={setComposerHeight}
                />
              </div>
            )}
          </div>
        )}

      </div>
      {/* Close main content column */}
      <ChatOverlays
        imagePreview={imagePreview}
        setImagePreview={setImagePreview}
        imagePreviewVisible={imagePreviewVisible}
        imagePreviewList={imagePreviewList}
        imagePreviewIndex={imagePreviewIndex}
        setImagePreviewIndex={setImagePreviewIndex}
        closeImagePreview={closeImagePreview}
        videoPreview={videoPreview}
        videoPreviewVisible={videoPreviewVisible}
        closeVideoPreview={closeVideoPreview}
        friendsOpen={friendsOpen}
        setFriendsOpen={setFriendsOpen}
        globalUserIds={globalUserIds}
        startDmWithUser={startDmWithUser}
        directCall={directCall}
        quickChannelOpen={quickChannelOpen}
        setQuickChannelOpen={setQuickChannelOpen}
        quickChannelBusy={quickChannelBusy}
        channelTypeOptions={channelTypeOptions}
        handleQuickChannelCreate={handleQuickChannelCreate}
        isSpaceOwner={isSpaceOwner}
        createSpaceOpen={createSpaceOpen}
        setCreateSpaceOpen={setCreateSpaceOpen}
        handleCreateSpace={handleCreateSpace}
        joinSpaceWithCode={joinSpaceWithCode}
        searchSpaces={searchSpaces}
        requestJoinSpace={requestJoinSpace}
        inputOpen={inputOpen}
        inputCfg={inputCfg}
        closeInput={closeInput}
        unlockModal={unlockModal}
        unlockingChannel={unlockingChannel}
        handleUnlockChannel={handleUnlockChannel}
        closeUnlockModal={closeUnlockModal}
        showJump={showJump}
        setShowJump={setShowJump}
        currentVoidId={currentVoidId}
        currentChannelId={currentChannelId}
        isMessageChannelType={isMessageChannelType}
        voiceJoined={voiceJoined}
        listRef={listRef}
        setUnreadMarkerByChan={setUnreadMarkerByChan}
        fq={fq}
        notificationsOpen={notificationsOpen}
        setNotificationsOpen={setNotificationsOpen}
        notificationItems={notificationItems}
        markAllRead={markAllRead}
        removeNotification={removeNotification}
        userQuickOpen={userQuickOpen}
        setUserQuickOpen={setUserQuickOpen}
        me={me}
        user={user}
        members={members}
        setMembers={setMembers}
        friendReqCount={friendReqCount}
        unreadNotificationCount={unreadNotificationCount}
        userStatusMessage={userStatusMessage}
        updateUserStatusMessage={updateUserStatusMessage}
        onOpenSettings={onOpenSettings}
        onOpenProfile={onOpenProfile}
        markAllChannelsRead={markAllChannelsRead}
        androidShare={androidShare}
        voids={voids}
        channels={channels}
        canAccessChannel={canAccessChannel}
        getDmDisplayName={getDmDisplayName}
        switchVoid={switchVoid}
        openChannelFromFqid={openChannelFromFqid}
        gifOpen={gifOpen}
        setGifOpen={setGifOpen}
        addRemoteAsUpload={addRemoteAsUpload}
      />


      <MobileSheets
        headerSpaceLabel={headerSpaceLabel}
        userQuickOpen={userQuickOpen}
        me={me}
        voids={voids}
        hiddenSpaces={hiddenSpaces}
        currentVoidId={currentVoidId}
        tabBgClass={tabBgClass}
        showDrawerTabs={showDrawerTabs}
        showPeople={showPeople}
        unread={unread}
        dmSearch={dmSearch}
        dmSearchOpen={dmSearchOpen}
        friendsOnline={friendsOnline}
        friendsList={friendsList}
        dmStartBusyUserId={dmStartBusyUserId}
        activeDmVoids={activeDmVoids}
        msgsByKey={msgsByKey}
        notified={notified}
        getDmDisplayName={getDmDisplayName}
        channelPaneView={channelPaneView}
        swipePanel={swipePanel}
        chanDrawerProgress={chanDrawerProgress}
        currentSpace={currentSpace}
        currentSpaceOnlineCount={currentSpaceOnlineCount}
        dmFriendSearch={dmFriendSearch}
        filteredFriendsForDmSearch={filteredFriendsForDmSearch}
        channels={channels}
        currentChannelId={currentChannelId}
        sidebarChannels={sidebarChannels}
        channelFolders={channelFolders}
        channelsLoading={channelsLoading}
        peopleLoading={peopleLoading}
        getVoiceNamesForChannel={getVoiceNamesForChannel}
        usersDrawerProgress={usersDrawerProgress}
        channelVisibleMembersWithPresence={channelVisibleMembersWithPresence}
        onlineChannelMembers={onlineChannelMembers}
        offlineChannelMembers={offlineChannelMembers}
        dmPageOpen={dmPageOpen}
        chanSheetOpen={chanSheetOpen}
        usersSheetOpen={usersSheetOpen}
        isDmSpace={isDmSpace}
        isSpaceOwner={isSpaceOwner}
        setChanSheetOpen={setChanSheetOpen}
        setUsersSheetOpen={setUsersSheetOpen}
        setDmPageOpen={setDmPageOpen}
        setUserQuickOpen={setUserQuickOpen}
        setDmSearch={setDmSearch}
        setDmSearchOpen={setDmSearchOpen}
        setDmFriendSearch={setDmFriendSearch}
        setCurrentVoidId={setCurrentVoidId}
        setCurrentChannelId={setCurrentChannelId}
        setChannelPaneView={setChannelPaneView}
        switchVoid={switchVoid}
        switchChannel={switchChannel}
        handleChannelReorder={handleChannelReorder}
        handleFolderReorder={handleFolderReorder}
        onOpenSettings={onOpenSettings}
        startDmWithUser={startDmWithUser}
        setFriendsOpen={setFriendsOpen}
        onOpenProfile={onOpenProfile}
      />
      <DirectCallOverlay
        phase={directCall.phase}
        peerName={directCall.peerName || dmPeerName || "Direct contact"}
        busy={directCall.busy}
        error={directCall.error}
        remoteCount={directCall.remoteCount}
        needsAudioUnlock={directCall.needsAudioUnlock}
        mediaState={directCall.mediaState}
        muted={directCall.muted}
        deafened={directCall.deafened}
        devicesOpen={directCall.devicesOpen}
        inputs={directCall.inputs}
        outputs={directCall.outputs}
        selectedInputId={directCall.selectedInputId}
        selectedOutputId={directCall.selectedOutputId}
        durationSeconds={directCall.durationSeconds}
        finalMessage={directCall.finalMessage}
        peerReconnecting={directCall.peerReconnecting}
        onUnlockAudio={directCall.unlockAudio}
        onAnswer={directCall.answerCall}
        onDecline={directCall.declineCall}
        onCancel={directCall.cancelCall}
        onEnd={directCall.endCall}
        onToggleMute={directCall.toggleMute}
        onToggleDeafen={directCall.toggleDeafen}
        onToggleDevices={directCall.toggleDevices}
        onRefreshDevices={directCall.refreshDevices}
        onSelectInput={(id) => { void directCall.switchMicrophone(id); }}
        onSelectOutput={(id) => { void directCall.applyOutputDevice(id); }}
        onDismissEnded={directCall.dismissEnded}
        audioRoute={directCall.audioRoute}
        availableRoutes={directCall.availableRoutes}
        onChangeAudioRoute={directCall.changeAudioRoute}
      />
    </div>

    {/* Mobile voice call dock — sits directly below the fixed channel header on small screens */}
    {voiceJoined && (
      <MobileVoiceDock
        voiceActiveChannelId={voiceActiveChannelId}
        voiceActiveChannelName={voiceActiveChannelName}
        voiceMuted={voiceMuted}
        videoEnabled={videoEnabled}
        videoBusy={videoBusy}
        // Navigation stays parent-side (switchVoid + handleSelectChannel)
        onJumpToCall={() => {
          if (voiceActiveSpaceId && voiceActiveSpaceId !== currentVoidId) switchVoid(voiceActiveSpaceId);
          if (voiceActiveChannelId) handleSelectChannel(voiceActiveChannelId);
        }}
        toggleMute={toggleMute}
        toggleVideo={toggleVideo}
        leaveVoice={leaveVoice}
      />
    )}

    {/* Voice channel switch confirmation modal */}
    {voiceSwitchPrompt && (() => {
      const targetChannelName = (() => {
        const found = channels.find((c: any) => c.id === voiceSwitchPrompt.channelId);
        if (found) return found.name;
        const parts = voiceSwitchPrompt.channelId.split(':');
        return parts[parts.length - 1] || voiceSwitchPrompt.channelId;
      })();
      const isSameSpace = voiceSwitchPrompt.spaceId === voiceActiveSpaceId;
      const targetSpaceName = voids.find((v) => v.id === voiceSwitchPrompt.spaceId)?.name || voiceSwitchPrompt.spaceId;
      const activeChannelName = voiceActiveChannelName || voiceActiveChannelId || 'voice channel';
      const activeSpaceName = voids.find((v) => v.id === voiceActiveSpaceId)?.name || voiceActiveSpaceId || 'current space';
      return (
        <VoiceSwitchModal
          targetChannelName={targetChannelName}
          targetSpaceName={targetSpaceName}
          activeChannelName={activeChannelName}
          activeSpaceName={activeSpaceName}
          isSameSpace={isSameSpace}
          onCancel={() => setVoiceSwitchPrompt(null)}
          onConfirmSwitch={confirmVoiceSwitch}
        />
      );
    })()}
    </>
  );
}

export default ChatLayout;

